import {
  ChatMode,
  type CapacityStatus,
  type ChatState,
} from '@/components/chat/types/types';
import type { NoteUpdateSource, NoteUpdateType } from '@/models/analytics/features/notes';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithStorage } from 'jotai/utils';

import { chatDraftStorage } from './chatDraftStorage';

// Simple atom to hold chat state across component mount/unmount cycles
// No storage, no serialization - just in-memory persistence for panel toggles
export const chatStateAtom = atom<ChatState>({});

// Atom family to store abort controllers for active streams, keyed by stateKey
// This allows abort controllers to persist across component mounts/unmounts
export const chatAbortControllersAtom = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_stateKey: string) => atom<AbortController | null>(null)
);

// Atom family to store File objects for sessionless chat messages, keyed by message ID
// This allows files to persist across component mounts/unmounts during navigation
export const sessionlessFilesAtom = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_messageId: string) => atom<File[] | null>(null)
);

// Atom family to store pre-uploaded attachment IDs for sessionless chat messages
// This allows attachment IDs to persist across component mounts/unmounts during navigation
export const sessionlessAttachmentIdsAtom = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_messageId: string) => atom<string[] | null>(null)
);

// Evidence Pro trial banner visibility - triggered when stream starts with Pro features
export const showEvidenceProTrialBannerAtom = atom<boolean>(false);

// Share modal visibility - controlled from both header and reply action buttons
export const isShareModalOpenAtom = atom<boolean>(false);
// Counts trial banner-eligible stream starts; banner shows every N-th query
export const evidenceProTrialBannerQueryCountAtom = atom<number>(0);

// Whether the mobile evidence input is "active" (focused or has text).
// Used by Chat.tsx to switch between idle (welcome + verbs) and active (full-view suggestions) layouts.
export const evidenceInputActiveAtom = atom<boolean>(false);

// Transient atom for passing a form template ID from the template picker to the
// next chat completion request. Set before sendMessage(), read and cleared in useChatStream.
export const pendingFormTemplateIdAtom = atom<string | null>(null);

// Controls the FormTemplatePicker modal visibility from anywhere (e.g. form_fill needs_template CTA)
export const formTemplatePickerOpenAtom = atom<boolean>(false);

/** Credits remaining popover (chat input quota badge) — open state shared so other code can control it. */
export const creditsRemainingPopoverOpenAtom = atom<boolean>(false);

/**
 * User preference for whether advanced clinical AI tools (which consume credits)
 * may be invoked automatically by Heidi. Read at stream-creation time and sent
 * to `/chat/completions` as `use_credits_when_needed`. Persisted to localStorage
 * so the choice survives navigation and refreshes; defaults to `true`.
 */
export const useCreditsWhenNeededAtom = atomWithStorage<boolean>(
  'heidi:use-credits-when-needed',
  true,
  undefined,
  { getOnInit: true }
);

/**
 * Tool-call ids whose template edit has been reverted from the chat card.
 * `true` is the optimistic in-flight lock; a number is the 1-based version
 * the revert landed on (rendered as "Reverted to v{N}").
 */
export const revertedTemplateEditToolCallIdsAtom = atomWithStorage<
  Record<string, number | true>
>('heidi:reverted-template-edit-tool-calls', {}, undefined, {
  getOnInit: true,
});

/**
 * Tool-call ids whose word-library update has been reverted from the chat card.
 * Presence means the Revert action already ran (or is in flight) for that call.
 */
export const revertedWordLibraryToolCallIdsAtom = atomWithStorage<Record<string, true>>(
  'heidi:reverted-word-library-tool-calls',
  {},
  undefined,
  {
    getOnInit: true,
  }
);

/**
 * Prior word-library values for keys touched by an `update_word_library` tool
 * call, captured while the call is still applying (before the BE write lands).
 * `null` means the key was absent (an add); a string means restore that value
 * on revert. Persisted so revert still works after a reload.
 */
export const wordLibraryPreviousValuesByToolCallIdAtom = atomWithStorage<
  Record<string, Record<string, string | null>>
>('heidi:word-library-previous-by-tool-call', {}, undefined, {
  getOnInit: true,
});

// Persistent atom for user's chat mode preference (Research vs Ask Heidi vs Auto)
// Defaults to AUTO mode (intelligent routing) and persists across sessions in localStorage
export const chatModeAtom = atomWithStorage<ChatMode>(
  'heidi-chat-mode-preference',
  ChatMode.EVIDENCE
);
export const newChatModeAtom = atomWithStorage<ChatMode>(
  'heidi-chat-mode-preference-new',
  ChatMode.AUTO
);

// Atom to store selected domains as a Set for chat filtering
// Starts empty, will be initialized with all domains when sources are first loaded
// This is the source of truth for domain selections
export const selectedDomainsAtom = atom<Set<string>>(new Set<string>());

// Derived atom: automatically converts selectedDomainsAtom (Set) to array format
// This eliminates the need for manual syncing between the two atoms
export const domainsAtom = atom<string[]>((get) => {
  const selected = get(selectedDomainsAtom);
  return Array.from(selected);
});

// ----- Derived atoms for selectedDomainsAtom operations -----
/**
 * Atom that sets the selected domains from a list of domains.
 *
 * @param domains - Array of domain strings to update the selection with
 */
export const setSelectedDomainsAtom = atom(null, (get, set, domains: string[]) => {
  const newSelected = new Set(domains);
  set(selectedDomainsAtom, newSelected);
});

/**
 * Atom that adds a list of domains to the current selection.
 *
 * @param domainsToAdd - Array of domain strings to add to selection
 */
export const addDomainsToSelectionAtom = atom(
  null,
  (get, set, domainsToAdd: string[]) => {
    const current = get(selectedDomainsAtom);
    const updated = new Set(current);

    // Add the specified domains
    domainsToAdd.forEach((domain) => updated.add(domain));

    set(selectedDomainsAtom, updated);
  }
);

/**
 * Atom that subtracts a list of domains from the current selection.
 *
 * @param domainsToSubtract - Array of domain strings to remove from selection
 */
export const subtractDomainsFromSelectionAtom = atom(
  null,
  (get, set, domainsToSubtract: string[]) => {
    const current = get(selectedDomainsAtom);
    const updated = new Set(current);

    // Subtract the specified domains
    domainsToSubtract.forEach((domain) => updated.delete(domain));

    set(selectedDomainsAtom, updated);
  }
);

// The LAUNCH protocol (`chatInputFocusTriggerAtom`, `pendingChatMessageAtom`,
// `pendingEntryMethodAtom`, `pendingChipMetaAtom`, `pendingChatImmediateAtom`,
// `isAnonymousChatAtom`) moved to `src/atoms/chatLaunchAtoms.ts`, and the
// SIDEBAR state (`chatSidebarOpenAtom`, `activeSidebarChatIdAtom`,
// `sidebarChatIdAtomFamily`) to `src/atoms/chatSidebarAtoms.ts`. Neither family
// is a Chat internal: product code writes them and Chat consumes them, so they
// are published as Chat's contract with a ratcheted export list instead of
// being twenty-two separate couplings to this module. Nothing is re-exported
// from here on purpose — a re-export would preserve every pair the move
// removes.

// Atom to trigger scroll to latest user message when incremented
// Used when pending messages are sent from floating input/chip clicks
export const scrollToUserMessageTriggerAtom = atom<number>(0);

// Atom to store custom selection view state in domain menu
export const showCustomSelectionAtom = atom<boolean>(false);

// `sourceSelectionModeAtom` and `presetSelectionAtom` moved to
// `src/atoms/sourceControlAtoms.ts` with the rest of the source-selection model.
// Their only production reader is `useEvidenceAnalytics`, which cannot leave Chat
// while it has to read a chat glob. Nothing is re-exported from here: a re-export
// would keep that reader resolving through this module and preserve the coupling.

// Custom domains atom - separate list for custom selection (independent from presets)
export const customDomainsAtom = atom<Set<string>>(new Set<string>());

// Atom to track which chats are currently streaming/thinking (supports multiple)
export const streamingChatIdsAtom = atom<Set<string>>(new Set<string>());

// Capacity status per chat ID — set from ChatResponse on load and from SSE capacity events during streaming
export const chatCapacityStatusAtomFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_chatId: string) => atom<CapacityStatus | null>(null)
);

/**
 * @deprecated This atom is no longer actively used. Chat history on mobile is now
 * handled by a Drawer in the mobile header. This atom can be removed once
 * ChatHistoryOverlay.tsx is removed.
 */
export const chatHistoryOverlayOpenAtom = atom<boolean>(false);

// ===== EVI-405: Source Control State for Chat Input =====
//
// Moved to `src/atoms/sourceControlAtoms.ts`. Ten production files outside Chat
// read that state and only that state, six of them writing it, so it is not
// Chat-private and does not belong in the module the quarantine watches by name.
// Nothing is re-exported from here on purpose: a re-export would preserve every
// pair the move removes.

/**
 * Atom to track if user has seen the post-trial tooltip (shown after trial expires).
 * Persists to localStorage so the tooltip only shows once per user.
 */
export const hasSeenPostTrialTooltipAtom = atomWithStorage(
  'heidi:post-trial-tooltip-seen',
  false
);

/**
 * Atom to track if the user has seen the introduction tooltip for the
 * share-chat button in the Evidence chat header. Persists to localStorage
 * so the tooltip only shows once per device.
 */
export const hasSeenShareChatCoachmarkAtom = atomWithStorage(
  'heidi:share-chat-coachmark-seen',
  false
);

/**
 * Increment to force trial source control banner to re-read chat count from storage
 * (e.g. after a stream completes and count is incremented).
 */
export const trialSourceControlBannerInvalidateAtom = atom<number>(0);

/**
 * Atom to control the sign-up modal visibility.
 * Used across multiple chat components to show/hide the sign-up modal.
 */
export const showSignUpModalAtom = atom<boolean>(false);

/**
 * Tracks whether the geo-block modal is visible (AU unauthenticated users).
 * Lifted to an atom so useEmbedMessaging can gate heidi:modalClosed correctly.
 */
export const showGeoBlockModalAtom = atom<boolean>(false);

// ============================================================================
// Chat Input Draft Persistence
// ============================================================================

/** Key used for new/sessionless chat drafts */
export const NEW_CHAT_DRAFT_KEY = 'new-chat';

/**
 * Atom family to store draft text content for chat inputs, keyed by chat ID.
 * Uses localStorage for persistence across page refreshes/navigation; see
 * `chatDraftStorage` for why writes are quota-safe and empty drafts are removed.
 * For new chats, use NEW_CHAT_DRAFT_KEY as the key.
 */
export const chatDraftTextAtomFamily = atomFamily((chatId: string) =>
  // Literal prefix kept inline so the persistence manifest records it verbatim.
  atomWithStorage<string>(`heidi-chat-draft-${chatId}`, '', chatDraftStorage)
);

/**
 * Atom family to store draft file attachments for chat inputs, keyed by chat ID.
 * Uses in-memory storage only (File objects can't be serialized to localStorage).
 * Files are preserved during navigation within the same session but lost on page refresh.
 */
export const chatDraftFilesAtomFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_chatId: string) => atom<File[]>([])
);

// ============================================================================
// Context Linking for Out-of-Session Evidence
// ============================================================================

/**
 * Linked session context for out-of-session Evidence queries.
 * When set, Evidence queries will include this session's context.
 */
export interface LinkedSessionContext {
  id: string;
  name: string;
  patientName?: string;
  createdAt?: string;
}

/**
 * Linked patient context for out-of-session Evidence queries.
 * When set, Evidence queries will include this patient's context.
 */
export interface LinkedPatientContext {
  id: string;
  name: string;
  demographicString?: string;
}

/**
 * Atom to store the currently linked session for out-of-session Evidence.
 */
export const linkedSessionContextAtom = atom<LinkedSessionContext | null>(null);

/**
 * Atom to store the currently linked patient for out-of-session Evidence.
 */
export const linkedPatientContextAtom = atom<LinkedPatientContext | null>(null);

/**
 * Atom to control the context linking popover visibility.
 * 'session' = show session picker, 'patient' = show patient picker, null = hidden
 */
export type ContextLinkingPopoverType = 'session' | 'patient' | null;
export const contextLinkingPopoverAtom = atom<ContextLinkingPopoverType>(null);

/**
 * Origin sentinel for context staged before its chat exists (new-chat
 * surfaces). ONE spelling site on purpose: the send-boundary provenance gate
 * fails closed, so a hand-typed variant ('Pending', 'pending') would not
 * throw or fail a type check — it would silently stop clinical context from
 * attaching. Always reference the constant or {@link linkedContextOriginFor}.
 */
export const PENDING_LINKED_CONTEXT_ORIGIN = 'PENDING';

/**
 * A linked-context origin: the sentinel, or a concrete chat id. The
 * `(string & {})` arm keeps chat ids assignable while surfacing the sentinel
 * in editor autocomplete.
 */
export type LinkedContextOrigin = typeof PENDING_LINKED_CONTEXT_ORIGIN | (string & {});

/**
 * The origin a staging surface records: the chat it stages for, or the
 * pending sentinel when the chat does not exist yet. The single home of the
 * `chatId ?? 'PENDING'` convention — picker surfaces and
 * `useChatContextLinking`'s setters all resolve through here.
 */
export const linkedContextOriginFor = (chatId?: string | null): LinkedContextOrigin =>
  chatId ?? PENDING_LINKED_CONTEXT_ORIGIN;

/**
 * Tracks which chat the linked context belongs to.
 * - null: no context linked
 * - {@link PENDING_LINKED_CONTEXT_ORIGIN}: context was linked on a new-chat
 *   surface, waiting for chat creation
 * - chatId: context belongs to this specific chat
 */
export const linkedContextOriginAtom = atom<LinkedContextOrigin | null>(null);

/**
 * Which linked context (session/patient) is marked as previewed.
 *
 * It drove a 38/62 ChatWithCanvas split with a read-only side panel until
 * `release-evidence-linked-context-canvas` was abandoned, and the panel that
 * rendered it is now deleted. The one live reader is `ChatInput`, which matches
 * this against the linked session/patient to give `LinkedContextBadge` its
 * `isActive` styling — but no production writer sets it non-null any more
 * (`openLinkedContextPreviewAtom` has no caller outside its own suite;
 * `clearLinkedContextAtom` writes only `null`), so `isActive` is always false.
 *
 * `null` means no linked context is marked active.
 */
export type LinkedContextPreview =
  | { type: 'session'; id: string }
  | { type: 'patient'; id: string }
  | null;

export const linkedContextPreviewAtom = atom<LinkedContextPreview>(null);

/**
 * Write-only atom to open (or replace) the linked-context preview panel.
 * Pass `null` to close.
 */
export const openLinkedContextPreviewAtom = atom(
  null,
  (_get, set, preview: LinkedContextPreview) => {
    set(linkedContextPreviewAtom, preview);
  }
);

/**
 * Write-only atom to close the linked-context preview panel.
 */
export const closeLinkedContextPreviewAtom = atom(null, (_get, set) => {
  set(linkedContextPreviewAtom, null);
});

/**
 * Write-only atom to clear all linked context.
 *
 * Also closes the preview panel so the right-hand canvas isn't left stranded
 * when the underlying link is dropped (chat switched, badge removed, etc.).
 */
export const clearLinkedContextAtom = atom(null, (_get, set) => {
  set(linkedSessionContextAtom, null);
  set(linkedPatientContextAtom, null);
  set(linkedContextOriginAtom, null);
  set(linkedContextPreviewAtom, null);
});

/**
 * Write-only staging setters — the ONLY sanctioned way for a picker surface
 * to stage a linked session/patient. They write the item, the single-slot
 * exclusion (staging one kind clears the other), and the provenance origin
 * atomically, so the send-boundary gate (`resolveAttachableLinkedContext`)
 * can rely on every populated staging carrying an origin. The gate fails
 * CLOSED on a missing origin: a raw `linkedSessionContextAtom` /
 * `linkedPatientContextAtom` write that skips the origin will stage a badge
 * that never attaches — stage through these instead.
 *
 * `origin` is the chat the staging targets, or the pending sentinel on a
 * new-chat surface — resolve it with {@link linkedContextOriginFor}.
 *
 * `useChatContextLinking`'s backend-sync deliberately does NOT use these:
 * it mirrors a chat's stored links field-by-field (no single-slot exclusion)
 * and stamps the origin itself.
 */
export const stageLinkedSessionAtom = atom(
  null,
  (_get, set, update: { session: LinkedSessionContext; origin: LinkedContextOrigin }) => {
    set(linkedSessionContextAtom, update.session);
    set(linkedPatientContextAtom, null);
    set(linkedContextOriginAtom, update.origin);
  }
);

/** See {@link stageLinkedSessionAtom}. */
export const stageLinkedPatientAtom = atom(
  null,
  (_get, set, update: { patient: LinkedPatientContext; origin: LinkedContextOrigin }) => {
    set(linkedPatientContextAtom, update.patient);
    set(linkedSessionContextAtom, null);
    set(linkedContextOriginAtom, update.origin);
  }
);

// ============================================================================
// Source Lists Selection (for Evidence source curation)
// ============================================================================

/**
 * Tracks which source lists are currently selected for the evidence query.
 * Source lists are additive - they combine with presets and each other.
 */
export const selectedSourceListIdsAtom = atom<Set<string>>(new Set<string>());

/**
 * Write-only atom to toggle a source list selection.
 */
export const toggleSourceListAtom = atom(null, (get, set, sourceListId: string) => {
  const current = get(selectedSourceListIdsAtom);
  const updated = new Set(current);
  if (updated.has(sourceListId)) {
    updated.delete(sourceListId);
  } else {
    updated.add(sourceListId);
  }
  set(selectedSourceListIdsAtom, updated);
});

/**
 * Write-only atom to clear all source list selections.
 */
export const clearSourceListsAtom = atom(null, (_get, set) => {
  set(selectedSourceListIdsAtom, new Set());
});

/**
 * Write-only atom to set specific source list IDs.
 */
export const setSourceListIdsAtom = atom(null, (_get, set, ids: string[]) => {
  set(selectedSourceListIdsAtom, new Set(ids));
});

/**
 * Atom to store cached source list data (populated by TanStack Query).
 * This allows derived atoms to access source list data without hooks.
 */
// TODO: Remove mock data once backend is ready
const MOCK_SOURCE_LISTS =
  process.env.NODE_ENV === 'development'
    ? [
        {
          id: 'mock-1',
          name: 'Cardiology Guidelines',
          domains: ['heart.org', 'acc.org', 'escardio.org'],
        },
        {
          id: 'mock-2',
          name: 'Oncology Sources',
          domains: ['cancer.gov', 'asco.org', 'esmo.org'],
        },
        {
          id: 'mock-3',
          name: 'Pediatrics',
          domains: ['aap.org', 'healthychildren.org'],
        },
      ]
    : [];

export const sourceListsDataAtom =
  atom<Array<{ id: string; name: string; domains: string[] }>>(MOCK_SOURCE_LISTS);

/**
 * Derived atom that computes the combined domains from all selected source lists.
 * These domains are additive - they combine with preset/custom selections.
 */
export const selectedSourceListDomainsAtom = atom((get) => {
  const selectedIds = get(selectedSourceListIdsAtom);
  const sourceLists = get(sourceListsDataAtom);

  if (selectedIds.size === 0) {
    return [];
  }

  const domains = new Set<string>();
  for (const list of sourceLists) {
    if (selectedIds.has(list.id)) {
      list.domains.forEach((d) => domains.add(d));
    }
  }
  return Array.from(domains);
});

// ============================================================================
// Chat First Messages (for Thread Selector display titles)
// ============================================================================

/**
 * Stores the first user message for each chat, keyed by chat ID.
 * Used by ThreadSelector to display meaningful titles instead of "New Chat".
 */
export const chatFirstMessagesAtom = atom<Record<string, string>>({});

/**
 * Write-only atom to set a chat's first message.
 */
export const setChatFirstMessageAtom = atom(
  null,
  (get, set, { chatId, firstMessage }: { chatId: string; firstMessage: string }) => {
    const current = get(chatFirstMessagesAtom);
    set(chatFirstMessagesAtom, { ...current, [chatId]: firstMessage });
  }
);

// ============================================================================
// Note Update Attribution (for Evidence analytics tracking)
// ============================================================================

/**
 * Attribution information for tracking the source of note updates.
 * Used to attribute note changes to Evidence chat actions (add/replace).
 */
export interface NoteUpdateAttribution {
  source: NoteUpdateSource;
  updateType: NoteUpdateType;
  timestamp: number;
}

/**
 * Atom family to store pending attribution for note updates, keyed by session ID.
 * When Evidence chat "Add to Note" or "Replace Note" is clicked, this is set.
 * NoteTabPane reads and clears this when firing trackNoteUpdated.
 */
export const noteUpdateAttributionAtomFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_sessionId: string) => atom<NoteUpdateAttribution | null>(null)
);

/**
 * Markdown-native editor: the exact note string a chat "Add to note" / "Replace
 * note" just wrote to the session cache, keyed by session ID. The editor's
 * content-sync focus guard suppresses cache updates while the user is focused so
 * a stale autosave echo can't reset the cursor mid-type — but that guard can't
 * tell a genuine external insert from an echo, so the insert is otherwise
 * swallowed until a re-hydration. The container force-applies the change once
 * this value matches the propagated content, then clears it. `null` = nothing
 * pending.
 */
export const pendingMarkdownNoteInsertAtomFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_sessionId: string) => atom<string | null>(null)
);

/**
 * Document-tab twin of {@link pendingMarkdownNoteInsertAtomFamily}, keyed by
 * document ID. A chat "Add to note" / "Replace note" into a markdown-native
 * document writes the new content to the document cache; the document editor's
 * focus guard would otherwise swallow that as an autosave echo, so the container
 * force-applies once this value matches the propagated content, then clears it.
 * `null` = nothing pending.
 */
export const pendingMarkdownDocumentInsertAtomFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_documentId: string) => atom<string | null>(null)
);
